import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useInventory } from '../context/InventoryContext';

export default function Dashboard() {
  const { currentUser } = useAuth();
  const containerRef = useRef(null);
  
  const { 
    subCompanies = [], 
    vehicles = [], 
    devices = [], 
    sims = [], 
    companies = [] 
  } = useInventory() || {};

  // Animation state
  const [animationPhase, setAnimationPhase] = useState('idle');
  const [showText, setShowText] = useState(false);
  const [isWaving, setIsWaving] = useState(false);
  const [carXPosition, setCarXPosition] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  // Get container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerWidth(rect.width);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const isSuperAdmin = useMemo(() => {
    if (!currentUser) return true;
    const rawRole = currentUser.role || currentUser.user_role || currentUser.type || '';
    const rolesArray = Array.isArray(rawRole) ? rawRole.map(r => String(r).toLowerCase()) : [String(rawRole).toLowerCase()];
    const hasSuperAdminToken = rolesArray.some(r => r.includes('super') || r.includes('root') || r.includes('owner'));
    const explicitlyRegularAdmin = rolesArray.some(r => r === 'admin');
    if (currentUser.isSuperAdmin === true || currentUser.is_super_admin === true) return true;
    if (currentUser.isSuperAdmin === false || currentUser.is_super_admin === false) return false;
    if (explicitlyRegularAdmin && !hasSuperAdminToken) return false;
    return hasSuperAdminToken || !(currentUser.companyId || currentUser.company_id || currentUser.parent_id);
  }, [currentUser]);

  const userCompanyId = currentUser?.companyId || currentUser?.company_id || currentUser?.parent_id || null;

  // Animation loop - Car drives from left to right
  useEffect(() => {
    if (containerWidth === 0) return;
    
    let timeouts = [];
    let animationId = null;

    const runAnimation = () => {
      setIsWaving(false);
      setShowText(false);
      
      const carWidth = 160;
      const startPos = -carWidth - 20;
      const centerPos = (containerWidth / 2) - (carWidth / 2);
      const endPos = containerWidth + 20;
      
      // Phase 1: Start from left
      setAnimationPhase('driving-in');
      setCarXPosition(startPos);
      
      // Phase 2: Drive to center (0-3s)
      timeouts.push(setTimeout(() => {
        let startTime = Date.now();
        const duration = 3000;
        const startPosAnim = startPos;
        const endPosAnim = centerPos;
        
        const animateToCenter = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const currentPos = startPosAnim + (endPosAnim - startPosAnim) * eased;
          setCarXPosition(currentPos);
          
          if (progress < 1) {
            animationId = requestAnimationFrame(animateToCenter);
          }
        };
        animateToCenter();
      }, 100));
      
      // Phase 3: Reach center, driver waves (3-3.5s)
      timeouts.push(setTimeout(() => {
        setIsWaving(true);
      }, 3000));
      
      // Phase 4: Say Namaskara (3.5-5.5s)
      timeouts.push(setTimeout(() => {
        setShowText(true);
        setAnimationPhase('greeting');
      }, 3500));
      
      // Phase 5: Stop waving, hide text (5.5-6s)
      timeouts.push(setTimeout(() => {
        setIsWaving(false);
        setShowText(false);
      }, 5500));
      
      // Phase 6: Drive to right (6-9s)
      timeouts.push(setTimeout(() => {
        setAnimationPhase('driving-out');
        let startTime = Date.now();
        const duration = 3000;
        const startPosAnim = centerPos;
        const endPosAnim = endPos;
        
        const animateToRight = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const currentPos = startPosAnim + (endPosAnim - startPosAnim) * eased;
          setCarXPosition(currentPos);
          
          if (progress < 1) {
            animationId = requestAnimationFrame(animateToRight);
          }
        };
        animateToRight();
      }, 6000));
      
      // Phase 7: Reset and loop (10s)
      timeouts.push(setTimeout(() => {
        setAnimationPhase('idle');
        setCarXPosition(startPos);
        timeouts.push(setTimeout(() => {
          runAnimation();
        }, 1000));
      }, 9500));
    };

    const initialDelay = setTimeout(runAnimation, 1000);
    timeouts.push(initialDelay);

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [containerWidth]);

  const allowedCompanies = useMemo(() => {
    if (isSuperAdmin || !userCompanyId) return companies;
    return companies.filter(c => String(c.id || c._id) === String(userCompanyId));
  }, [companies, isSuperAdmin, userCompanyId]);

  const allowedSubCompanies = useMemo(() => {
    if (isSuperAdmin || !userCompanyId) return subCompanies;
    return subCompanies.filter(sub => {
      const parentId = String(sub.parent_company_id || sub.parent_id || sub.company_id || sub.companyId);
      return parentId === String(userCompanyId);
    });
  }, [subCompanies, isSuperAdmin, userCompanyId]);

  const [selectedMainCompanyId, setSelectedMainCompanyId] = useState(null);
  const [isMainDropdownOpen, setIsMainDropdownOpen] = useState(false);
  const [mainSearchTerm, setMainSearchTerm] = useState('');
  const [selectedSubCompanyId, setSelectedSubCompanyId] = useState(null);
  const [isSubDropdownOpen, setIsSubDropdownOpen] = useState(false);
  const [subSearchTerm, setSubSearchTerm] = useState('');

  const loading = companies.length === 0 && subCompanies.length === 0 && devices.length === 0;

  const availableSubCompanies = useMemo(() => {
    const targetMainId = selectedMainCompanyId || (!isSuperAdmin ? userCompanyId : null);
    if (!targetMainId) return isSuperAdmin ? [] : allowedSubCompanies;
    return allowedSubCompanies.filter(sub => {
      const parentId = String(sub.parent_company_id || sub.parent_id || sub.company_id || sub.companyId);
      return parentId === String(targetMainId);
    });
  }, [selectedMainCompanyId, allowedSubCompanies, isSuperAdmin, userCompanyId]);

  const handleMainCompanySelect = (id) => {
    setSelectedMainCompanyId(id);
    setSelectedSubCompanyId(null);
    setIsMainDropdownOpen(false);
  };

  const metrics = useMemo(() => {
    const userCompIdStr = userCompanyId ? String(userCompanyId).trim() : null;
    const childSubCompanyIds = selectedMainCompanyId 
      ? allowedSubCompanies
          .filter(sub => String(sub.parent_company_id || sub.parent_id || sub.company_id || sub.companyId).trim() === String(selectedMainCompanyId).trim())
          .map(sub => String(sub.id || sub._id).trim())
      : [];
    
    const baseVehicles = isSuperAdmin || !userCompIdStr ? vehicles : vehicles.filter(v => {
      const vId = String(v.company || v.company_id || v.companyId || v.parent_id || '').trim();
      return vId === userCompIdStr || allowedSubCompanies.some(sub => String(sub.id || sub._id).trim() === vId);
    });

    const baseDevices = isSuperAdmin || !userCompIdStr ? devices : devices.filter(d => {
      const dId = String(d.company || d.company_id || d.companyId || '').trim();
      return dId === userCompIdStr || allowedSubCompanies.some(sub => String(sub.id || sub._id).trim() === dId);
    });

    const baseSims = isSuperAdmin || !userCompIdStr ? sims : sims.filter(s => {
      const sId = String(s.company || s.company_id || s.companyId || s.parent_company_id || s.parent_id || '').trim();
      return sId === userCompIdStr || allowedSubCompanies.some(sub => String(sub.id || sub._id).trim() === sId);
    });

    let fVehicles = baseVehicles;
    let fDevices = baseDevices;
    let fSims = baseSims;

    if (selectedSubCompanyId) {
      const subStr = String(selectedSubCompanyId).trim();
      fVehicles = vehicles.filter(v => String(v.company || v.company_id || v.companyId || v.parent_id || '').trim() === subStr);
      fDevices = devices.filter(d => String(d.company || d.company_id || d.companyId || '').trim() === subStr);
      fSims = sims.filter(s => String(s.company || s.company_id || s.companyId || s.parent_company_id || s.parent_id || '').trim() === subStr);
    } 
    else if (selectedMainCompanyId) {
      const mainStr = String(selectedMainCompanyId).trim();
      fVehicles = vehicles.filter(v => {
        const vId = String(v.company || v.company_id || v.companyId || v.parent_id || '').trim();
        return vId === mainStr || childSubCompanyIds.includes(vId);
      });
      fDevices = devices.filter(d => {
        const dId = String(d.company || d.company_id || d.companyId || '').trim();
        return dId === mainStr || childSubCompanyIds.includes(dId);
      });
      fSims = sims.filter(s => {
        const sId = String(s.company || s.company_id || s.companyId || s.parent_company_id || s.parent_id || '').trim();
        return sId === mainStr || childSubCompanyIds.includes(sId);
      });
    }

    const devStatus = { newStock: 0, available: 0, inUse: 0, discarded: 0 };
    const simStatus = { newStock: 0, available: 0, inUse: 0, safeCustody: 0, suspended: 0, scrap: 0 };

    fDevices.forEach(d => {
      const s = (d.status || d.device_status || '').toLowerCase();
      if (s.includes('new')) devStatus.newStock++;
      else if (s.includes('avail')) devStatus.available++;
      else if (s.includes('discard')) devStatus.discarded++;
      else devStatus.inUse++;
    });

    fSims.forEach(s => {
      const st = (s.status || s.sim_status || '').toLowerCase();
      if (st.includes('new')) simStatus.newStock++;
      else if (st.includes('avail')) simStatus.available++;
      else if (st.includes('custody')) simStatus.safeCustody++;
      else if (st.includes('suspend')) simStatus.suspended++;
      else if (st.includes('scrap')) simStatus.scrap++;
      else simStatus.inUse++;
    });

    return { vCount: fVehicles.length, dCount: fDevices.length, sCount: fSims.length, devStatus, simStatus };
  }, [vehicles, devices, sims, isSuperAdmin, userCompanyId, allowedSubCompanies, selectedMainCompanyId, selectedSubCompanyId]);

  const getCarClasses = () => {
    switch(animationPhase) {
      case 'driving-in': return 'opacity-100';
      case 'greeting': return 'opacity-100';
      case 'driving-out': return 'opacity-100';
      default: return 'opacity-0';
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Syncing Cordon NX Inventory...</div>;

  return (
    <div className="w-full min-h-screen bg-gray-50 p-2 sm:p-4 text-gray-800 font-sans overflow-hidden">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
        <div className="flex-1 w-full">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-xs text-gray-400 mt-0.5">
            Welcome back, {currentUser?.username || currentUser?.email || 'User'} ({isSuperAdmin ? 'Super Admin View' : 'Company Admin View'})
          </p>
        </div>
      </div>

      {/* HORIZONTAL LINE WITH CAR ANIMATION */}
      <div ref={containerRef} className="relative my-3 sm:my-4 overflow-visible">
        <hr className="border-gray-300" />
        
        {/* Car Animation on the line */}
        <div className="absolute left-0 right-0 -top-[22px] overflow-visible">
          <div className="relative w-full" style={{ height: '45px' }}>
            <div 
              className={`absolute top-1/2 -translate-y-1/2 ${getCarClasses()}`}
              style={{ 
                transform: `translateX(${carXPosition}px) translateY(-50%)`,
              }}
            >
              <div className="relative" style={{ width: '160px', height: '45px' }}>
                {/* Shadow */}
                <div className="absolute -bottom-1 left-[10px] w-[130px] h-[4px] bg-black/15 rounded-full blur-sm"></div>
                
                {/* Super Car Body - FIXED ORIENTATION: Headlights on RIGHT */}
                <div className="absolute bottom-0 left-0 w-[140px] h-[22px] bg-gradient-to-b from-red-500 to-red-700 rounded-xl shadow-lg">
                  {/* 3D Top surface */}
                  <div className="absolute -top-[6px] left-[10px] w-[110px] h-[6px] bg-gradient-to-b from-red-400 to-red-500 rounded-t-xl"></div>
                  
                  {/* Racing stripe */}
                  <div className="absolute top-[4px] left-1/2 -translate-x-1/2 w-[3px] h-[14px] bg-white/20 rounded-full"></div>
                  
                  {/* Windows with driver visible - Driver is on LEFT side (right-hand drive) */}
                  <div className="absolute -top-[14px] left-[25px] w-[28px] h-[14px] bg-gradient-to-b from-blue-200 to-blue-300 rounded-t-lg border border-blue-400/50 shadow-inner">
                    {/* Driver face visible through window */}
                    <div className="absolute bottom-[1px] left-1/2 -translate-x-1/2 w-[14px] h-[10px] bg-yellow-200 rounded-full border border-blue-300">
                      {/* Eyes */}
                      <div className="absolute top-[2px] left-[2px] w-[3px] h-[3px] bg-black rounded-full">
                        <div className="absolute -top-[0.5px] -right-[0.5px] w-[1.5px] h-[1.5px] bg-white rounded-full"></div>
                      </div>
                      <div className="absolute top-[2px] right-[2px] w-[3px] h-[3px] bg-black rounded-full">
                        <div className="absolute -top-[0.5px] -right-[0.5px] w-[1.5px] h-[1.5px] bg-white rounded-full"></div>
                      </div>
                      {/* Smile */}
                      <div className="absolute bottom-[1px] left-1/2 -translate-x-1/2 w-[6px] h-[3px] border-b-2 border-red-400 rounded-full"></div>
                      {/* Hair */}
                      <div className="absolute -top-[1px] left-0 w-full h-[3px] bg-black rounded-full"></div>
                    </div>
                  </div>
                  <div className="absolute -top-[14px] left-[60px] w-[28px] h-[14px] bg-gradient-to-b from-blue-200 to-blue-300 rounded-t-lg border border-blue-400/50 shadow-inner">
                    <div className="absolute top-[2px] right-[4px] w-[6px] h-[4px] bg-white/20 rounded-full"></div>
                  </div>
                  
                  {/* Driver's arm waving from LEFT window */}
                  <div className={`absolute -top-[10px] left-[28px] transition-all duration-300 origin-bottom ${isWaving ? 'animate-wave-arm-driver' : ''}`}
                       style={{ 
                         transform: isWaving ? 'rotate(-35deg)' : 'rotate(0deg)',
                         transformOrigin: 'bottom center'
                       }}>
                    <div className="w-[8px] h-[14px] bg-yellow-200 rounded-full border border-yellow-300">
                      <div className="absolute -top-[5px] left-1/2 -translate-x-1/2 w-[7px] h-[7px] bg-yellow-200 rounded-full border border-yellow-300"></div>
                      {/* Fingers */}
                      <div className="absolute -top-[6px] -left-[3px] w-[2px] h-[4px] bg-yellow-100 rounded-full"></div>
                      <div className="absolute -top-[6px] right-[1px] w-[2px] h-[4px] bg-yellow-100 rounded-full"></div>
                    </div>
                  </div>
                  
                  {/* Door */}
                  <div className="absolute left-[55px] top-0 w-[30px] h-[22px] bg-gradient-to-r from-red-500 to-red-600 rounded-md border border-red-700/30">
                    <div className="absolute right-[3px] top-1/2 -translate-y-1/2 w-[4px] h-[3px] bg-gray-300 rounded-full"></div>
                  </div>
                  
                  {/* Headlights - On the RIGHT side (front of car) */}
                  <div className="absolute -right-[2px] top-[5px] w-[5px] h-[6px] bg-gradient-to-r from-yellow-300 to-yellow-200 rounded-r-full shadow-lg shadow-yellow-300/50">
                    <div className="absolute -top-[1px] right-[1px] w-[2px] h-[2px] bg-white/50 rounded-full"></div>
                  </div>
                  <div className="absolute -right-[2px] top-[13px] w-[5px] h-[6px] bg-gradient-to-r from-yellow-300 to-yellow-200 rounded-r-full shadow-lg shadow-yellow-300/50">
                    <div className="absolute -top-[1px] right-[1px] w-[2px] h-[2px] bg-white/50 rounded-full"></div>
                  </div>
                  
                  {/* Taillights - On the LEFT side (back of car) */}
                  <div className="absolute -left-[2px] top-[5px] w-[5px] h-[6px] bg-gradient-to-l from-red-600 to-red-500 rounded-l-full shadow-lg shadow-red-500/30"></div>
                  <div className="absolute -left-[2px] top-[13px] w-[5px] h-[6px] bg-gradient-to-l from-red-600 to-red-500 rounded-l-full shadow-lg shadow-red-500/30"></div>
                  
                  {/* Spoiler - On the LEFT side (back) */}
                  <div className="absolute -left-[6px] -top-[8px] w-[12px] h-[8px] bg-gradient-to-b from-gray-700 to-gray-900 rounded-b-md shadow-md"></div>
                  
                  {/* Exhaust pipes - On the LEFT side (back) */}
                  <div className="absolute -left-[4px] bottom-[3px] w-[6px] h-[3px] bg-gray-700 rounded-full"></div>
                  <div className="absolute -left-[4px] bottom-[10px] w-[6px] h-[3px] bg-gray-700 rounded-full"></div>
                  
                  {/* Alloy Wheels */}
                  <div className="absolute -bottom-[6px] left-[20px] w-[16px] h-[16px] bg-gradient-to-b from-gray-300 to-gray-500 rounded-full border-2 border-gray-600 shadow-lg animate-spin-slow">
                    <div className="absolute inset-[2px] bg-gray-400 rounded-full">
                      <div className="absolute inset-[3px] bg-gray-300 rounded-full border border-gray-500">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[4px] h-[4px] bg-gray-600 rounded-full"></div>
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1.5px] h-[8px] bg-gray-500"></div>
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1.5px] h-[8px] bg-gray-500"></div>
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[8px] h-[1.5px] bg-gray-500"></div>
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[8px] h-[1.5px] bg-gray-500"></div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute -bottom-[6px] right-[20px] w-[16px] h-[16px] bg-gradient-to-b from-gray-300 to-gray-500 rounded-full border-2 border-gray-600 shadow-lg animate-spin-slow">
                    <div className="absolute inset-[2px] bg-gray-400 rounded-full">
                      <div className="absolute inset-[3px] bg-gray-300 rounded-full border border-gray-500">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[4px] h-[4px] bg-gray-600 rounded-full"></div>
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1.5px] h-[8px] bg-gray-500"></div>
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1.5px] h-[8px] bg-gray-500"></div>
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[8px] h-[1.5px] bg-gray-500"></div>
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[8px] h-[1.5px] bg-gray-500"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Namaskara Text Bubble */}
                {showText && (
                  <div className="absolute -top-[55px] left-[30px] animate-float-up z-10">
                    <div className="relative bg-gradient-to-r from-purple-50 to-blue-50 px-3 py-1.5 rounded-xl shadow-lg border-2 border-blue-400">
                      <span className="text-sm font-bold text-blue-600 whitespace-nowrap"> 🫵🏻 Welcome to Cordon Support ! 🙇‍♂️ </span>
                      <div className="absolute -bottom-2 left-[60px] w-3 h-3 bg-white border-b-2 border-r-2 border-blue-400 rotate-45"></div>
                    </div>
                  </div>
                )}

                {/* Road */}
                <div className="absolute -bottom-[4px] left-0 w-full h-[2px] bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin-slow {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes wave-arm-driver {
          0% { transform: rotate(-35deg) scale(1); }
          25% { transform: rotate(-50deg) scale(1.1); }
          50% { transform: rotate(-25deg) scale(1); }
          75% { transform: rotate(-45deg) scale(1.1); }
          100% { transform: rotate(-35deg) scale(1); }
        }
        
        @keyframes float-up {
          0% { transform: translateY(10px) scale(0.7); opacity: 0; }
          100% { transform: translateY(0px) scale(1); opacity: 1; }
        }
        
        .animate-spin-slow {
          animation: spin-slow 2s linear infinite;
        }
        
        .animate-wave-arm-driver {
          animation: wave-arm-driver 0.6s ease-in-out infinite;
        }
        
        .animate-float-up {
          animation: float-up 0.5s ease-out forwards;
        }
      `}</style>

      {/* REST OF THE DASHBOARD - Keep the same */}
      <div className="mb-6 sm:mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Filter Scope</label>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start">
          <div className="relative w-full sm:w-80">
            <div 
              className={`bg-white border border-gray-300 rounded-md p-2 flex justify-between items-center select-none ${isSuperAdmin ? 'cursor-pointer' : 'bg-gray-100 cursor-not-allowed'}`}
              onClick={() => isSuperAdmin && setIsMainDropdownOpen(!isMainDropdownOpen)}
            >
              <span className="text-sm truncate">
                {selectedMainCompanyId 
                  ? allowedCompanies.find(c => (c.id || c._id) === selectedMainCompanyId)?.name 
                  : !isSuperAdmin ? allowedCompanies[0]?.name || "Your Assigned Company" : "All Main Companies"}
              </span>
              {isSuperAdmin && <span>▼</span>}
            </div>
            
            {isSuperAdmin && isMainDropdownOpen && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                <input 
                  className="w-full p-2 border-b text-sm outline-none" 
                  placeholder="Filter Main Companies..." 
                  value={mainSearchTerm} 
                  onChange={(e) => setMainSearchTerm(e.target.value)} 
                />
                <div 
                  className="p-2 cursor-pointer text-blue-600 font-bold text-sm border-b hover:bg-gray-50" 
                  onClick={() => {
                    setSelectedMainCompanyId(null);
                    setSelectedSubCompanyId(null);
                    setIsMainDropdownOpen(false);
                  }}
                >
                  Clear Filter (Show All)
                </div>
                {allowedCompanies
                  .filter(c => (c.name || '').toLowerCase().includes(mainSearchTerm.toLowerCase()))
                  .map(c => (
                    <div 
                      key={c.id || c._id} 
                      className="p-2 text-sm hover:bg-gray-100 cursor-pointer" 
                      onClick={() => handleMainCompanySelect(c.id || c._id)}
                    >
                      {c.name || c.company_name}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {((isSuperAdmin && selectedMainCompanyId) || (!isSuperAdmin)) && availableSubCompanies.length > 0 && (
            <div className="relative w-full sm:w-80">
              <div 
                className="bg-white border border-gray-300 rounded-md p-2 flex justify-between items-center cursor-pointer border-purple-300 bg-purple-50/10 select-none" 
                onClick={() => setIsSubDropdownOpen(!isSubDropdownOpen)}
              >
                <span className="text-sm truncate">
                  {selectedSubCompanyId 
                    ? availableSubCompanies.find(s => (s.id || s._id) === selectedSubCompanyId)?.name 
                    : "All Sub Companies"}
                </span>
                <span>▼</span>
              </div>
              
              {isSubDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  <input 
                    className="w-full p-2 border-b text-sm outline-none" 
                    placeholder="Filter Sub Companies..." 
                    value={subSearchTerm} 
                    onChange={(e) => setSubSearchTerm(e.target.value)} 
                  />
                  <div 
                    className="p-2 cursor-pointer text-purple-600 font-bold text-sm border-b hover:bg-gray-50" 
                    onClick={() => {
                      setSelectedSubCompanyId(null);
                      setIsSubDropdownOpen(false);
                    }}
                  >
                    Clear Sub Filter ({isSuperAdmin ? 'Main Company Only' : 'Show All Mine'})
                  </div>
                  {availableSubCompanies
                    .filter(s => (s.name || '').toLowerCase().includes(subSearchTerm.toLowerCase()))
                    .map(s => (
                      <div 
                        key={s.id || s._id} 
                        className="p-2 text-sm hover:bg-gray-100 cursor-pointer" 
                        onClick={() => {
                          setSelectedSubCompanyId(s.id || s._id);
                          setIsSubDropdownOpen(false);
                        }}
                      >
                        {s.name || s.company_name}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
        {!selectedMainCompanyId && !selectedSubCompanyId && (
          <div className="bg-white p-3 sm:p-5 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase truncate">My Companies</p>
            <p className="text-xl sm:text-2xl font-bold mt-1 sm:mt-2 text-blue-600">{allowedCompanies.length}</p>
          </div>
        )}
        {((!selectedMainCompanyId && !selectedSubCompanyId) || (selectedMainCompanyId && !selectedSubCompanyId && availableSubCompanies.length > 0)) && (
          <div className="bg-white p-3 sm:p-5 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase truncate">Sub Companies</p>
            <p className="text-xl sm:text-2xl font-bold mt-1 sm:mt-2 text-purple-600">
              {selectedMainCompanyId ? availableSubCompanies.length : allowedSubCompanies.length}
            </p>
          </div>
        )}
        <div className="bg-white p-3 sm:p-5 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase truncate">Devices</p>
          <p className="text-xl sm:text-2xl font-bold mt-1 sm:mt-2 text-green-600">{metrics.dCount}</p>
        </div>
        <div className="bg-white p-3 sm:p-5 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase truncate">SIMs</p>
          <p className="text-xl sm:text-2xl font-bold mt-1 sm:mt-2 text-amber-500">{metrics.sCount}</p>
        </div>
        <div className="bg-white p-3 sm:p-5 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase truncate">Vehicles</p>
          <p className="text-xl sm:text-2xl font-bold mt-1 sm:mt-2 text-rose-600">{metrics.vCount}</p>
        </div>
      </div>

      <div className="space-y-6 sm:space-y-8">
        <section className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700 mb-3 sm:mb-4 border-b pb-2">Devices Inventory Status</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {['newStock', 'available', 'inUse', 'discarded'].map(key => (
              <div key={key} className="bg-gray-50 p-3 sm:p-4 rounded-lg border hover:bg-gray-100 transition-colors">
                <div className="text-[10px] sm:text-xs uppercase font-bold text-gray-500 truncate">{key}</div>
                <div className="text-lg sm:text-2xl font-bold">{metrics.devStatus[key]}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700 mb-3 sm:mb-4 border-b pb-2">Sims Network Status</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3 text-center">
            {['newStock', 'available', 'inUse', 'safeCustody', 'suspended', 'scrap'].map(key => (
              <div key={key} className="bg-gray-50 p-2 sm:p-3 rounded-lg border hover:bg-gray-100 transition-colors">
                <div className="text-[10px] sm:text-xs uppercase font-bold text-gray-500 truncate">{key}</div>
                <div className="text-base sm:text-xl font-bold">{metrics.simStatus[key]}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}